'use client';

import React, { useState, useEffect } from 'react';
import { Send, Loader2, X, CheckCircle2 } from 'lucide-react';
import { Offer } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
  offer: Offer;
  isLoggedIn: boolean;
  userName?: string;
  userEmail?: string;
  prefillBody?: string;
}

interface FormState {
  subject: string;
  body: string;
  name: string;
  email: string;
}

export default function InquiryModal({
  isOpen,
  onClose,
  offer,
  isLoggedIn,
  onSent,
  userName,
  userEmail,
  prefillBody,
}: InquiryModalProps) {
  const { user } = useAuth();

  const buildInitial = (): FormState => ({
    subject: `Anfrage: ${offer.title}`,
    body: prefillBody || '',
    name: userName || '',
    email: userEmail || '',
  });

  const [form, setForm] = useState<FormState>(buildInitial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitial());
      setSent(false);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userName, userEmail, prefillBody]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!form.subject.trim()) { setError('Bitte einen Betreff eingeben.'); return; }
    if (!form.body.trim()) { setError('Bitte eine Nachricht eingeben.'); return; }
    if (!isLoggedIn) {
      if (!form.name.trim()) { setError('Bitte deinen Namen eingeben.'); return; }
      if (!form.email.trim()) { setError('Bitte deine E-Mail-Adresse eingeben.'); return; }
    }

    setSending(true);
    setError(null);

    try {
      const res = await fetch('/api/inquiry/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject.trim(),
          body: form.body.trim(),
          offerId: offer.id,
          offerTitle: offer.title,
          senderId: user?.id || null,
          senderName: form.name.trim() || null,
          senderEmail: form.email.trim() || null,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler beim Senden.');

      setSent(true);
      setTimeout(() => {
        if (onSent) onSent();
      }, 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Senden. Bitte versuche es erneut.';
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setError(null);
      setForm(buildInitial());
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center px-3"
      style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: '420px',
          background: '#1e2028',
          borderRadius: '20px 20px 0 0',
          border: '1px solid rgba(255,255,255,0.08)',
          borderBottom: 'none',
          maxHeight: '90vh',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.18)' }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
          <h2 className="text-lg font-bold text-white">Anfrage senden</h2>
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'rgba(255,255,255,0.1)' }}
            aria-label="Schließen"
          >
            <X size={16} color="rgba(255,255,255,0.7)" />
          </button>
        </div>

        {sent ? (
          /* ── Success state ── */
          <div className="flex flex-col items-center py-12 gap-4 text-center px-5 flex-1">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(163,196,74,0.15)' }}
            >
              <CheckCircle2 size={32} color="#a3c44a" />
            </div>
            <div>
              <p className="text-base font-bold text-white mb-1">Anfrage gesendet!</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Wir haben deine Anfrage erhalten und melden uns so schnell wie möglich.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="mt-2 px-8 py-3 rounded-full text-sm font-bold transition-all duration-150 active:scale-[0.98]"
              style={{ background: '#a3c44a', color: '#1a1c22' }}
            >
              Schließen
            </button>
          </div>
        ) : (
          <>
            {/* ── Scrollable form body ── */}
            <div
              className="overflow-y-auto flex-1 px-5 pb-4 space-y-4"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Offer badge */}
              <div
                className="flex items-center gap-2 px-4 py-3 rounded-xl"
                style={{
                  background: 'rgba(163,196,74,0.08)',
                  border: '1px solid rgba(163,196,74,0.2)',
                }}
              >
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Angebot:</span>
                <span className="text-sm font-semibold truncate" style={{ color: '#a3c44a' }}>
                  {offer.title}
                </span>
              </div>

              {/* Betreff */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Betreff <span style={{ color: '#a3c44a' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={handleChange('subject')}
                  placeholder="z.B. Frage zum Angebot"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                  }}
                />
              </div>

              {/* Nachricht */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Nachricht <span style={{ color: '#a3c44a' }}>*</span>
                </label>
                <textarea
                  value={form.body}
                  onChange={handleChange('body')}
                  placeholder="Deine Fragen oder Anmerkungen zum Angebot..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl text-sm resize-none outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                  }}
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Zusätzliche Kontaktdaten (optional)
                </span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
              </div>

              {isLoggedIn && (
                <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Du bist angemeldet. Fülle diese Felder nur aus, wenn du auch außerhalb der App kontaktiert werden möchtest.
                </p>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Name{!isLoggedIn && <span style={{ color: '#a3c44a' }}> *</span>}
                  {isLoggedIn && <span className="font-normal text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}> (optional)</span>}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={handleChange('name')}
                  placeholder="Dein Name"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                  }}
                />
              </div>

              {/* E-Mail */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  E-Mail{!isLoggedIn && <span style={{ color: '#a3c44a' }}> *</span>}
                  {isLoggedIn && <span className="font-normal text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}> (optional)</span>}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  placeholder="deine@email.de"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                  }}
                />
              </div>

              {/* Error */}
              {error && (
                <p
                  className="text-xs font-medium px-4 py-3 rounded-xl"
                  style={{
                    background: 'rgba(239,68,68,0.12)',
                    color: '#f87171',
                    border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  {error}
                </p>
              )}
            </div>

            {/* ── Submit button — pinned ── */}
            <div
              className="flex-shrink-0 px-5 pt-3"
              style={{
                paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))',
              }}
            >
              <button
                type="button"
                onClick={handleSubmit}
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-full text-base font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-60"
                style={{ background: '#a3c44a', color: '#1a1c22' }}
              >
                {sending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {sending ? 'Wird gesendet...' : 'Anfrage absenden'}
              </button>
              <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                Deine Nachricht wird an das WINEWOOD Team gesendet.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
